import noop from 'lodash/noop';
import { createContext, useContext, useEffect, useState } from 'react';
import { createIntl, createIntlCache, RawIntlProvider } from 'react-intl';

import { CustomersWithCustomElementsE } from '../declarations/enums/common';
import { LocaleE } from '../declarations/enums/locale';
import useReactRouter from '../hooks/useReactRouter';
import messagesEn from '../locale/en.json';
import messagesEs from '../locale/es.json';
import messageFr from '../locale/fr.json';
import messagesPt from '../locale/pt.json';
import { useCommon } from './CommonContext';

type LangT = keyof typeof LocaleE;

type MessagesT = {
  [keyLang in LangT]: {
    [key in string]: string;
  };
};

type LangOptionT = {
  value: LocaleE;
  label: string;
};

type LocaleContextT = {
  selectedLocale: LangT;
  langOptions: LangOptionT[];
  onChangeLocale: (val: LangT) => void;
};

const MESSAGES: MessagesT = {
  en: messagesEn,
  es: messagesEs,
  pt: messagesPt,
  fr: messageFr,
};

const DEFAULT_LOCALE = LocaleE.en;
const CFC_LOCALE = 'cfc_locale';

const LANG_LIST = {
  [LocaleE.en]: 'English',
  [LocaleE.pt]: 'Portuguese',
  [LocaleE.es]: 'Spanish',
  [LocaleE.fr]: 'French',
};

const customerLangList = {
  [CustomersWithCustomElementsE.WAYAJ_EARTH_FRIENDLY_TRAVEL]: [
    LocaleE.en,
    LocaleE.pt,
    LocaleE.es,
    LocaleE.fr,
  ],
  [CustomersWithCustomElementsE.MURAL_TO]: [LocaleE.en, LocaleE.es, LocaleE.fr],
  [CustomersWithCustomElementsE.MONDI_LODGE]: [LocaleE.en, LocaleE.es],
  [CustomersWithCustomElementsE.TAMARIND_TREE_DOMINICA]: [
    LocaleE.en,
    LocaleE.pt,
    LocaleE.es,
    LocaleE.fr,
  ],
  [CustomersWithCustomElementsE.INTER_AMERICAN_INSTITUTE_FOR_COOPERATION]: [
    LocaleE.en,
    LocaleE.pt,
    LocaleE.es,
  ],
  [CustomersWithCustomElementsE.ILHA_DE_TOQUE_ECO_BOTIQUE_HOTEL_SAO_SEBASTIAO]: [
    LocaleE.en,
    LocaleE.pt,
  ],
  [CustomersWithCustomElementsE.SIX_SENSES_DOURO_VALLEY_SAMODAES]: [
    LocaleE.en,
    LocaleE.pt,
    LocaleE.fr,
  ],
};

const getLangOptions = (slugUrl: keyof typeof customerLangList) => {
  if (customerLangList[slugUrl]) {
    return customerLangList[slugUrl].map(item => ({
      value: item,
      label: LANG_LIST[item],
    }));
  }

  return [
    {
      value: LocaleE.en,
      label: LANG_LIST[LocaleE.en],
    },
  ];
};

// // This is optional but highly recommended
// // since it prevents memory leak
const cache = createIntlCache();

const LocaleCtx = createContext<LocaleContextT>({
  selectedLocale: DEFAULT_LOCALE,
  langOptions: [],
  onChangeLocale: () => noop,
});

const LocaleProvider: React.FC = ({ children }) => {
  const { setQuery, getUrlQueryParams } = useReactRouter();
  const { lang } = getUrlQueryParams<{ lang: LangT }>();
  const { slugUrl } = useCommon();

  const [selectedLocale, setLocale] = useState<LangT>(
    (localStorage.getItem(CFC_LOCALE) as LangT) || lang || DEFAULT_LOCALE
  );
  const langOptions = getLangOptions(slugUrl as keyof typeof customerLangList);

  const intl = createIntl(
    {
      locale: navigator.language,
      messages: MESSAGES[selectedLocale],
      defaultLocale: DEFAULT_LOCALE,
    },
    cache
  );

  const onChangeLocale = (val: LangT) => {
    setQuery({ lang: val }, true);
    localStorage.setItem(CFC_LOCALE, val);
    setLocale(val);
  };

  useEffect(() => {
    if (!langOptions.find(({ value }) => value === selectedLocale)) {
      onChangeLocale(LocaleE.en);
    }
  }, []);

  const value = {
    selectedLocale,
    langOptions,
    onChangeLocale,
  };

  return (
    <LocaleCtx.Provider value={value}>
      <RawIntlProvider value={intl}>{children}</RawIntlProvider>
    </LocaleCtx.Provider>
  );
};

export default LocaleProvider;

export const useLocale = () => useContext(LocaleCtx);
